import React, { useMemo, useState } from 'react';
import { Button, Card, Empty, Modal, Popconfirm, Select, Table, Tag, Typography, Input, Space, message } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  useGetOrderLineItemNotesQuery,
  useAddOrderLineItemNoteMutation,
  useDeleteOrderLineItemNoteMutation,
  useCopyOrderLineItemNotesMutation,
  useLazyGetOrderLineItemsQuery,
} from '../../../redux/api/order-line-item';

const { TextArea } = Input;
const { Title } = Typography;

type Props = {
  lineItemId: string;
};

export default function OrderLineItemNotes({ lineItemId }: Props) {
  const { data: notes, refetch, isFetching } = useGetOrderLineItemNotesQuery(lineItemId, { skip: !lineItemId });
  const [addNote, { isLoading: isAdding }] = useAddOrderLineItemNoteMutation();
  const [deleteNote, { isLoading: isDeleting }] = useDeleteOrderLineItemNoteMutation();
  const [copyNotes, { isLoading: isCopying }] = useCopyOrderLineItemNotesMutation();

  const [text, setText] = useState('');
  const [copyOpen, setCopyOpen] = useState(false);
  const [selectedNoteIds, setSelectedNoteIds] = useState<string[]>([]);
  const [targetOptions, setTargetOptions] = useState<{ label: string; value: string }[]>([]);
  const [selectedTargets, setSelectedTargets] = useState<{ label: string; value: string }[]>([]);
  const [getLineItems, { isFetching: isSearching }] = useLazyGetOrderLineItemsQuery();

  const columns = useMemo(
    () => [
      {
        title: 'Note Text',
        render: (r: any) => r.text,
      },
      {
        title: 'Created Date',
        render: (r: any) => (r.created_time ? dayjs(r.created_time).format('YYYY-MM-DD HH:mm') : '-'),
      },
      {
        title: 'Created By',
        render: (r: any) => r?.created_by?.full_name || r?.created_by?.email || '-',
      },
      {
        title: 'Actions',
        align: 'right' as const,
        render: (r: any) => (
          <Popconfirm
            title="Delete this note?"
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={async () => {
              await deleteNote({ id: lineItemId, noteId: r.id }).unwrap();
              refetch();
            }}
          >
            <Button type="text" icon={<DeleteOutlined />} danger loading={isDeleting} />
          </Popconfirm>
        ),
      },
    ],
    [deleteNote, isDeleting, lineItemId, refetch]
  );

  const onAdd = async () => {
    const value = text.trim();
    if (!value) return;
    try {
      await addNote({ id: lineItemId, text: value }).unwrap();
      setText('');
      refetch();
    } catch (e) {
      message.error('Failed to add note');
    }
  };

  const onSearchTargets = async (value: string) => {
    if (!value || value.length < 2) {
      setTargetOptions([]);
      return;
    }
    try {
      const res: any = await getLineItems({ page: 1, search: value, partsbadger_production_method: 'Swiss' }).unwrap();
      const options = (res?.results || [])
        .filter((li: any) => li.id !== lineItemId)
        .map((li: any) => ({ label: `${li.order?.name || ''} — ${li.name}`, value: li.id }));
      const merged = [...options];
      selectedTargets.forEach((sel) => {
        if (!merged.find((o) => o.value === sel.value)) merged.push(sel);
      });
      setTargetOptions(merged);
    } catch (e) {
      setTargetOptions([]);
    }
  };

  const doCopy = async () => {
    try {
      await copyNotes({ note_ids: selectedNoteIds, target_line_item_ids: selectedTargets.map((t) => t.value) }).unwrap();
      setCopyOpen(false);
      message.success('Notes copied');
    } catch (e) {
      message.error('Failed to copy notes');
    }
  };

  return (
    <Card className="mt-4" title={<div className="flex items-center justify-between w-full">
      <span>Notes</span>
      <Space>
        <Button onClick={() => setCopyOpen(true)} disabled={!notes || notes.length === 0 || selectedNoteIds.length === 0}>Copy</Button>
      </Space>
    </div>}>
      <div className="mb-3">
        <TextArea rows={3} placeholder="Add a note" value={text} onChange={(e) => setText(e.target.value)} />
        <div className="mt-2">
          <Button type="primary" onClick={onAdd} loading={isAdding} disabled={!text.trim()}>Add Note</Button>
        </div>
      </div>

      <Table
        size="small"
        rowKey={(r) => r.id}
        columns={columns as any}
        dataSource={notes || []}
        loading={isFetching}
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No notes" /> }}
        pagination={false}
        rowSelection={{
          selectedRowKeys: selectedNoteIds,
          onChange: (keys: React.Key[]) => setSelectedNoteIds(keys as string[]),
        }}
      />

      <Modal
        title="Copy Notes to Jobs"
        open={copyOpen}
        onCancel={() => setCopyOpen(false)}
        onOk={doCopy}
        okButtonProps={{ disabled: selectedNoteIds.length === 0 || selectedTargets.length === 0, loading: isCopying }}
      >
        <div className="mb-2">Select target job(s)</div>
        <Select
          mode="multiple"
          showSearch
          filterOption={false}
          onSearch={onSearchTargets}
          options={targetOptions}
          loading={isSearching}
          labelInValue
          value={selectedTargets}
          onChange={(v) => setSelectedTargets(v as { label: string; value: string }[])}
          placeholder="Search by order or part name"
          style={{ width: '100%' }}
        />
        <div className="mt-3">
          <Tag color="blue">Selected Notes: {selectedNoteIds.length}</Tag>
        </div>
      </Modal>
    </Card>
  );
}
